import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { format } from 'date-fns/format';
import { queries } from '@/utils/queryOptions';
import { DateRangePicker } from '@frontend/ui/components/date-picker-range';
import { ContainerList } from '@/components/Containers/ContainerList';
import { CardWithSimpleMetric } from '@/components/Cards/CardWithSimpleMetric';
import { Container } from '@/components/Containers/Container';
import { CardWithOverTimeChart } from '@/components/Cards/CardWithOverTimeChart';
import { HomeSkeleton } from '@/components/Skeletons/HomeSkeleton';
import { RoomGroupUsage, Threshold } from '@frontend/api-client-user/types';
import { getHumanTreshold, getThresholdKeyFromUsage } from '@/utils/thresholds';
import { add, isBefore, isWithinInterval } from 'date-fns';
import { CreateChartObject } from '@/components/Charts/ChartOverTime';
import { ErrorRoute } from '@/components/ErrorRoute';

const searchSchema = z.object({
	start: z
		.string()
		.default(format(add(new Date(), { days: -30 }), 'yyyy-MM-dd')),
	end: z.string().default(format(new Date(), 'yyyy-MM-dd')),
});

export const Route = createFileRoute('/_protected/home')({
	validateSearch: searchSchema,
	beforeLoad: () => ({ crumb: 'Dashboard' }),
	loaderDeps: ({ search }) => search,
	loader: async ({ context, deps }) => {
		const start = new Date(deps.start);
		const end = new Date(deps.end);
		await context.queryClient.ensureQueryData(
			queries['room-groups-usage'].list(start, end),
		);
		await context.queryClient.ensureQueryData(
			queries['room-groups'].thresholds,
		);
		await context.queryClient.ensureQueryData(
			queries['sections'].thresholds([deps.sectionId]),
		);
		return { start, end };
	},
	pendingComponent: HomeSkeleton,
	errorComponent: ErrorRoute,
	component: Index,
});

function Index() {
	const navigate = Route.useNavigate();
	const { start, end } = Route.useLoaderData();
	const { sectionId } = Route.useSearch();

	const { usage, thresholds, sectionThold } = useSuspenseQueries({
		queries: [
			queries['room-groups-usage'].list(start, end),
			queries['room-groups'].thresholds,
			queries['sections'].thresholds([sectionId]),
		],
		combine: (result) => {
			const usage = result['0'];
			const thresholds = result['1'];
			const sectionThold = result['2'];

			return {
				usage: usage.data,
				thresholds: thresholds.data,
				sectionThold: sectionThold.data,
			};
		},
	});

	const thresholdKeys = thresholds.items.reduce(
		(acc, t) => {
			acc[t.id] = t.usageThresholds;
			return acc;
		},
		{} as Record<string, Threshold[]>,
	);

	type ChartData = CreateChartObject<RoomGroupUsage>;

	const mapped: Record<string, ChartData> = {};

	for (const item of usage.items) {
		for (const usage of item.usage) {
			const existing = mapped[usage.date];
			if (existing) {
				existing.alcohol += usage.alcohol;
				existing.soap += usage.soap;
			} else {
				const threshold = sectionThold.items[0]?.usageThresholds.find((x) =>
					isWithinInterval(usage.date, { start: x.start, end: x.end }),
				);
				mapped[usage.date] = {
					...usage,
					thresholdUpper: threshold?.upper,
					thresholdLower: threshold?.lower,
				};
			}
		}
	}

	const chartData = Object.values(mapped).sort((a, b) =>
		isBefore(b.date, a.date) ? 1 : -1,
	);

	return (
		<>
			<Container>
				<DateRangePicker
					initialDateFrom={start}
					initialDateTo={end}
					onUpdate={({ range }) =>
						navigate({
							search: (prev) => ({
								...prev,
								start: format(range.from, 'yyyy-MM-dd'),
								end: format(range.to ?? range.from, 'yyyy-MM-dd'),
							}),
						})
					}
				/>
			</Container>
			<ContainerList>
				{usage.items.map((item) => {
					const threshold = getThresholdKeyFromUsage(
						item.usage,
						thresholdKeys[item.id],
					);
					return (
						<CardWithSimpleMetric
							key={item.id + item.name}
							title={item.name}
							subtitle={getHumanTreshold(threshold)}
							chartData={item.usage.map((s) => ({
								value: s.alcohol + s.soap,
								date: s.date,
							}))}
							onAction={() =>
								navigate({
									to: '/group',
									search: (prev) => ({ sectionId: prev.sectionId }),
								})
							}
							color={
								threshold === 'Low'
									? 'threshold-low'
									: threshold === 'Medium'
										? 'threshold-medium'
										: threshold === 'High'
											? 'threshold-high'
											: 'muted-foreground'
							}
						/>
					);
				})}
			</ContainerList>
			<Container>
				<CardWithOverTimeChart
					title="Usage"
					subtitle="Total usage for period"
					chartData={chartData as ChartData[]}
					chartConfig={{
						alcohol: { color: 'chart-1', label: 'Alcohol' },
						soap: { color: 'chart-2', label: 'Soap' },
					}}
				/>
			</Container>
		</>
	);
}
